<template>
  <div class="sign-up pop-up">
    <div class="sign-up__inner">
      <div class="sign-up__content">
        <div class="sign-up__logo-text-box">
          <a class="sign-up__logo" href="/">
            <img
              class="sign-up__logo-img"
              src="@/assets.v2/images/logo-sign-up.svg"
              alt="Shortform"
            />
          </a>
        </div>
      </div>
      <div class="sign-up__form-box">
        <div class="shortform-sign-up">
          <h2 class="shortform-sign-up__title">{{ title }}</h2>
          <div v-if="deletionConfirmationComplete">
            <p class="confirmation-completed-text">
              Your account has been deleted. You won't be able to log in anymore
              with this email. You can always create a new account if you want
              to join Shortform again.
            </p>
            <router-link
              :to="{ name: 'signup' }"
              class="btns btns--largest btns--full account-deletion-btn"
              data-test-id="ok-button"
              >Ok</router-link
            >
          </div>
          <div v-else>
            <p>Are you sure you want to delete your account?</p>
            <div v-if="accountStatus === null">
              <FormError
                :errors="validation.errors"
                field="delete_token"
                class="error-message"
              />
              <FormError
                :errors="validation.errors"
                field="_schema"
                class="error-message"
              />
              <router-link
                v-if="hasError"
                :to="{ name: 'billing' }"
                class="btns btns--largest btns--full account-deletion-btn"
                data-test-id="cancel-subscription"
                >Go to the billing page</router-link
              >
            </div>
            <div v-else>
              <p><AccountStatusInfo :account-status="accountStatus" /></p>
              <p v-if="hasError" class="error-message">
                {{
                  'Failed to delete account. Please try again or contact support at help@shortform.com.'
                }}
              </p>
              <router-link
                v-if="hasError"
                :to="{ name: 'billing' }"
                class="btns btns--largest btns--full account-deletion-btn"
                data-test-id="cancel-subscription"
                >Go to the billing page</router-link
              >
              <form
                v-else
                class="shortform-sign-up__form"
                @submit.prevent="confirmDeletion"
              >
                <div class="account-deletion-confirmation__group">
                  <router-link
                    :to="{ name: 'discover' }"
                    class="btns btns--largest btns--full account-deletion-btn"
                    data-test-id="keep-account"
                    >Keep my account</router-link
                  >
                </div>
                <div class="account-deletion-confirmation__group">
                  <button
                    v-wait:disabled="'Deleting'"
                    type="submit"
                    class="btns btns--largest btns--red btns--full account-deletion-btn"
                    data-test-id="delete-account"
                  >
                    <v-wait for="Deleting">
                      <template v-slot:waiting>
                        Deleting
                        <button-spinner />
                      </template>
                      <span>Delete account</span>
                    </v-wait>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onBeforeMount, onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useWait } from '@/app/composables/useWait'

import { authCssFix } from '@/helpers/html'

import FormError from '@/components/ui/FormError.vue'
import { backend } from '@/services/backend'
import { ValidationState } from '@/helpers/form'
import type { AccountStatus } from '@/models/interfaces'
import { Sentry } from '@/services/sentry'
import AccountStatusInfo from '@/app/components/account/AccountStatusInfo.vue'
import { notificationService } from '@/services/notification'
import { localLogout } from '@/services/auth'

const route = useRoute()
const wait = useWait()

const validation = ref(new ValidationState())
const accountStatus = ref<AccountStatus | null>(null)
const deletionConfirmationComplete = ref(false)
const hasError = ref(false)

onBeforeMount(async () => {
  try {
    const token = route.query.delete_token
    if (token) {
      accountStatus.value = await backend.getAccountStatusByDeleteToken(
        token as string,
      )
    } else {
      accountStatus.value = await backend.getAccountStatus()
    }
  } catch (error) {
    // The error here is usually NetworkError or Timeout, but
    // we still capture all errors to Sentry to be aware if
    // error rate increases.
    Sentry.withScope(function (scope) {
      scope.setFingerprint(['account-status-loading-error'])
      Sentry.captureException(error)
    })
    hasError.value = true
    validation.value.showErrors(error)
  }
})

onMounted(() => {
  authCssFix()
})

async function confirmDeletion(): Promise<void> {
  validation.value.reset()

  try {
    wait.start('Deleting')
    await notificationService.unsubscribe()

    const token = route.query.delete_token
    await backend.confirmAccountDeletion(token as string)
    deletionConfirmationComplete.value = true

    try {
      await localLogout()
    } catch (err) {
      // Ignore any errors raised here.
    }

    wait.end('Deleting')
  } catch (error) {
    wait.end('Deleting')
    hasError.value = true
    Sentry.captureException(error)
    validation.value.showErrors(error)
  }
}

const title = computed(() => {
  return deletionConfirmationComplete.value
    ? 'Account Deleted'
    : 'Confirm Account Deletion'
})
</script>

<style scoped lang="scss">
.confirmation-completed-text {
  text-align: left;
}

.account-deletion-btn {
  font-size: 16px;
  max-width: 240px;
  font-weight: 500;
  line-height: 22px;
}

.account-deletion-confirmation__group {
  text-align: center;
  margin-bottom: 8px;
}

.error-message {
  color: $c-red-6;
  background-color: $c-red-2;
  padding: 8px;
  border-radius: 4px;
  margin-top: 10px;
  margin-bottom: 10px;
  font-weight: normal;
}
</style>
